import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Key } from "react";
import {
  Grid,
  Card,
  Row,
  Col,
  Typography,
  Empty,
  Skeleton,
  Tag,
  Checkbox,
  Badge,
  Button,
} from "antd";
import {
  ClockCircleOutlined,
  BellOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import { AppState } from "store";
import { Table, TablePagination } from "components/base";
import {
  getCategories,
  initializeNotifications,
  setNotificationsSelectedPage,
  setNotificationDynamicFilters,
  setSelectedNotifications,
  setBulkActionsNotifications,
} from "store/actions";
import { Notificatons } from "../../../types";
import { TableProps } from "antd/lib/table";
import useDebounce from "utils/hooks";
import { parseNotificationTable } from "../../../utils/parsers/fcm_notification_parser";

import "styles/components/tab-content/notification-content/notification-content.scss";

const { useBreakpoint } = Grid;
const { Text, Paragraph } = Typography;

// Desktop/Tablet Table Columns
const getColumns = (isTablet: boolean): TableProps<any>["columns"] => [
  {
    title: "Category",
    dataIndex: "category",
    width: isTablet ? 80 : 100,
    ellipsis: true,
    render: (text: string, record: { is_read: boolean }) => (
      <span className={`category-cell ${!record.is_read ? "unread" : ""}`}>
        {!record.is_read && <span className="unread-indicator">*</span>}
        {text}
      </span>
    ),
  },
  {
    title: "Sub Category",
    dataIndex: "subCategory",
    width: isTablet ? 100 : 130,
    ellipsis: true,
    render: (text: string, record: { is_read: boolean }) => (
      <span className={!record.is_read ? "unread-text" : ""}>{text}</span>
    ),
  },
  {
    title: "Date & Time",
    dataIndex: "notification_time",
    width: isTablet ? 140 : 175,
    ellipsis: true,
    render: (text: string, record: { is_read: boolean }) => (
      <span className={!record.is_read ? "unread-text" : ""}>{text}</span>
    ),
  },
  {
    title: "Priority",
    dataIndex: "priority",
    width: isTablet ? 80 : 100,
    ellipsis: true,
    render: (text: string, record: { is_read: boolean }) => (
      <PriorityTag priority={text} isUnread={!record.is_read} />
    ),
  },
  {
    title: "Description",
    dataIndex: "body",
    width: isTablet ? 200 : 340,
    align: "left",
    render: (text: string, record: { is_read: boolean }) => (
      <Paragraph
        className={`description-cell ${!record.is_read ? "unread-text" : ""}`}
        ellipsis={{ rows: 5 }}
      >
        {text}
      </Paragraph>
    ),
  },
];

// Priority Tag Component
interface PriorityTagProps {
  priority: string;
  isUnread?: boolean;
}

const PriorityTag = ({ priority, isUnread = false }: PriorityTagProps) => {
  const getColor = () => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "red";
      case "medium":
        return "orange";
      case "low":
        return "green";
      default:
        return "default";
    }
  };

  return (
    <Tag
      color={getColor()}
      className={`priority-tag ${isUnread ? "unread-tag" : ""}`}
    >
      {priority}
    </Tag>
  );
};

// Notification Card Component for Mobile
interface NotificationCardProps {
  notification: any;
  isSelected: boolean;
  onSelect: (key: Key, checked: boolean) => void;
}

const NotificationCard = ({
  notification,
  isSelected,
  onSelect,
}: NotificationCardProps) => {
  const isUnread = !notification.is_read;

  const handleCheckboxChange = (e: any) => {
    e.stopPropagation();
    onSelect(notification.key, e.target.checked);
  };

  return (
    <Card
      className={`notification-card ${isUnread ? "unread" : ""} ${
        isSelected ? "selected" : ""
      }`}
      size="small"
    >
      <div className="card-checkbox" onClick={(e) => e.stopPropagation()}>
        <Checkbox checked={isSelected} onChange={handleCheckboxChange} />
      </div>

      {isUnread && <div className="unread-dot" />}

      <div className="card-content">
        <div className="card-header">
          <div className="category-info">
            <Text strong className={`category-text ${isUnread ? "unread" : ""}`}>
              {notification.category}
            </Text>
            {notification.subCategory && (
              <Text type="secondary" className="subcategory-text">
                {notification.subCategory}
              </Text>
            )}
          </div>
          <PriorityTag priority={notification.priority} isUnread={isUnread} />
        </div>

        <div className="card-body">
          <Paragraph
            className={`description-text ${isUnread ? "unread" : ""}`}
            ellipsis={{ rows: 2 }}
          >
            {notification.body}
          </Paragraph>
        </div>

        <div className="card-footer">
          <div className="datetime-info">
            <ClockCircleOutlined className="datetime-icon" />
            <Text type="secondary" className="datetime-text">
              {notification.notification_time}
            </Text>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Loading Skeleton for Mobile
const MobileSkeleton = () => (
  <div className="mobile-skeleton">
    {[1, 2, 3, 4].map((item) => (
      <Card key={item} className="notification-card">
        <Skeleton active paragraph={{ rows: 2 }} />
      </Card>
    ))}
  </div>
);

// Mobile List View
interface MobileViewProps {
  data: any[];
  loading?: boolean;
  //  Changed: current page keys + total selected count
  currentPageSelectedKeys: Key[];
  totalSelectedCount: number;
  onSelectItem: (key: Key, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
}

const MobileView = ({
  data,
  loading,
  currentPageSelectedKeys,
  totalSelectedCount,
  onSelectItem,
  onSelectAll,
}: MobileViewProps) => {
  if (loading) {
    return <MobileSkeleton />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="empty-state">
        <Empty
          description="No notifications found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  //  "Select all" only refers to the current page
  const allSelected =
    data.length > 0 && currentPageSelectedKeys.length === data.length;
  const someSelected =
    currentPageSelectedKeys.length > 0 &&
    currentPageSelectedKeys.length < data.length;

  return (
    <div className="mobile-notifications-list">
      {/* Select All Header */}
      <div className="select-all-header">
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => onSelectAll(e.target.checked)}
        >
          <Text type="secondary">
            {totalSelectedCount > 0
              ? `${totalSelectedCount} selected${
                  currentPageSelectedKeys.length !== totalSelectedCount
                    ? ` (${currentPageSelectedKeys.length} on this page)`
                    : ""
                }`
              : "Select All"}
          </Text>
        </Checkbox>
      </div>

      {/* Notification Cards */}
      <div className="notifications-cards">
        {data.map((notification, index) => (
          <NotificationCard
            key={notification.key || index}
            notification={notification}
            //  Check against current page keys
            isSelected={currentPageSelectedKeys.includes(notification.key)}
            onSelect={onSelectItem}
          />
        ))}
      </div>
    </div>
  );
};

// Main TableView Component
interface TableViewProps {
  selectedRows: Key[];
  onChangeSelection: (selectedKeys: Key[]) => void;
}
export default function TableView({
  selectedRows,
  onChangeSelection,
}: TableViewProps) {
  const dispatch = useDispatch();
  const screens = useBreakpoint();

  const isMobile = !screens.md;
  const isTablet = Boolean(screens.md && !screens.lg);

  const { notificationCenter } = useSelector<AppState, AppState>(
    (state) => state
  );

  const {
    notificationsCenter,
    page,
    search,
    categories,
    subCategories,
    filters: { updated },
  } = notificationCenter;

  const loading: boolean = (notificationCenter as any).loading ?? false;

  const data = useMemo(
    () => parseNotificationTable(notificationsCenter),
    [notificationsCenter]
  );

  const { selectedPage, totalPages } = page;

  //  Internal Set to track selected keys across all pages
  const [allSelectedKeys, setAllSelectedKeys] = useState<Set<Key>>(
    () => new Set(selectedRows)
  );

  //  NEW: Cache to store raw data of selected items across ALL pages
  const [selectedItemsCache, setSelectedItemsCache] = useState<
    Map<Key, any>
  >(() => new Map());

  useEffect(() => {
    setAllSelectedKeys(new Set(selectedRows));
  }, [selectedRows]);

  //  NEW: Whenever current page data loads, update cache for any
  //    items on this page that are already selected
  useEffect(() => {
    if (!data) return;
    setSelectedItemsCache((prevCache) => {
      const newCache = new Map(prevCache);
      data.forEach((item: any) => {
        if (allSelectedKeys.has(item.key)) {
          newCache.set(item.key, item.raw || item);
        }
      });
      return newCache;
    });
  }, [data, allSelectedKeys]);

  const currentPageSelectedKeys = useMemo(() => {
    if (!data) return [];
    return data
      .filter((item: any) => allSelectedKeys.has(item.key))
      .map((item: any) => item.key);
  }, [data, allSelectedKeys]);

  const totalCount = data?.length ?? 0;
  const selectedCount = allSelectedKeys.size;
  const currentPageSelectedCount = currentPageSelectedKeys.length;

  const onBack = useCallback(() => {
    if (selectedPage > 1) {
      dispatch(setNotificationsSelectedPage(selectedPage - 1));
    }
  }, [dispatch, selectedPage]);

  const onNext = useCallback(() => {
    if (selectedPage < totalPages) {
      dispatch(setNotificationsSelectedPage(selectedPage + 1));
    }
  }, [dispatch, selectedPage, totalPages]);

  const resetCallback = useCallback(() => {
    dispatch(setNotificationsSelectedPage(1));
  }, [dispatch]);

  const debounceSearchString = useDebounce(search, 1000);

  useEffect(() => {
    dispatch(getCategories());
  }, [dispatch]);

  useEffect(() => {
    console.log("sub Category in Table view:", subCategories);
  }, [subCategories, dispatch]);

  useEffect(() => {
    dispatch(initializeNotifications(selectedPage, updated, true));
  }, [dispatch, selectedPage, updated, debounceSearchString]);

  //  UPDATED: Sync Redux using the cache — includes ALL pages
  const syncReduxSelection = useCallback(
    (newKeys: Set<Key>, updatedCache: Map<Key, any>) => {
      if (newKeys.size > 0) {
        dispatch(setBulkActionsNotifications(true));

        //  Pull from cache for ALL selected keys (not just current page)
        const selectedItems: (Notificatons | null)[] = [];
        newKeys.forEach((key) => {
          const cached = updatedCache.get(key);
          if (cached) {
            selectedItems.push(cached);
          }
        });

        console.log("selected Items (all pages):", selectedItems);
        dispatch(setSelectedNotifications(selectedItems));
      } else {
        dispatch(setBulkActionsNotifications(false));
        dispatch(setSelectedNotifications([]));
      }
    },
    [dispatch]
  );

  //  UPDATED: Handle table selection change — update cache + merge
  const handleSelectionChange = useCallback(
    (newPageKeys: Key[]) => {
      const newAllKeys = new Set(allSelectedKeys);
      const newCache = new Map(selectedItemsCache);

      const currentPageKeys = new Set(
        (data ?? []).map((item: any) => item.key)
      );

      // Remove all current page keys first
      currentPageKeys.forEach((key) => {
        newAllKeys.delete(key);
        newCache.delete(key); // ✅ Remove from cache too
      });

      // Add back only the ones now selected on this page
      newPageKeys.forEach((key) => {
        newAllKeys.add(key);
        //  Store raw data in cache
        const item = data?.find((d: any) => d.key === key);
        if (item) {
          newCache.set(key, item.raw || item);
        }
      });

      setAllSelectedKeys(newAllKeys);
      setSelectedItemsCache(newCache);
      onChangeSelection(Array.from(newAllKeys));
      syncReduxSelection(newAllKeys, newCache);
    },
    [data, allSelectedKeys, selectedItemsCache, onChangeSelection, syncReduxSelection]
  );

  //  UPDATED: Clear ALL selections + cache
  const clearAllSelections = useCallback(() => {
    setAllSelectedKeys(new Set());
    setSelectedItemsCache(new Map());
    onChangeSelection([]);
    dispatch(setBulkActionsNotifications(false));
    dispatch(setSelectedNotifications([]));
  }, [dispatch, onChangeSelection]);

  //  UPDATED: Mobile single item selection — update cache
  const handleMobileSelectItem = useCallback(
    (key: Key, checked: boolean) => {
      const newAllKeys = new Set(allSelectedKeys);
      const newCache = new Map(selectedItemsCache);

      if (checked) {
        newAllKeys.add(key);
        const item = data?.find((d: any) => d.key === key);
        if (item) {
          newCache.set(key, item.raw || item);
        }
      } else {
        newAllKeys.delete(key);
        newCache.delete(key);
      }

      setAllSelectedKeys(newAllKeys);
      setSelectedItemsCache(newCache);
      onChangeSelection(Array.from(newAllKeys));
      syncReduxSelection(newAllKeys, newCache);
    },
    [allSelectedKeys, selectedItemsCache, data, onChangeSelection, syncReduxSelection]
  );

  //  UPDATED: Mobile select all — update cache
  const handleMobileSelectAll = useCallback(
    (checked: boolean) => {
      const newAllKeys = new Set(allSelectedKeys);
      const newCache = new Map(selectedItemsCache);
      const currentPageItems = data ?? [];

      if (checked) {
        currentPageItems.forEach((item: any) => {
          newAllKeys.add(item.key);
          newCache.set(item.key, item.raw || item);
        });
      } else {
        currentPageItems.forEach((item: any) => {
          newAllKeys.delete(item.key);
          newCache.delete(item.key);
        });
      }

      setAllSelectedKeys(newAllKeys);
      setSelectedItemsCache(newCache);
      onChangeSelection(Array.from(newAllKeys));
      syncReduxSelection(newAllKeys, newCache);
    },
    [data, allSelectedKeys, selectedItemsCache, onChangeSelection, syncReduxSelection]
  );

  const columns = useMemo(() => getColumns(isTablet), [isTablet]);

  const getScrollConfig = () => {
    if (isTablet) {
      return { y: "60vh", x: 600 };
    }
    return { y: "70vh" };
  };

  return (
    <div className="notifications-table-container">
      {isMobile ? (
        <MobileView
          data={data}
          loading={loading}
          currentPageSelectedKeys={currentPageSelectedKeys}
          totalSelectedCount={selectedCount}
          onSelectItem={handleMobileSelectItem}
          onSelectAll={handleMobileSelectAll}
        />
      ) : (
        <Table
          className="notifications-table"
          columns={columns}
          dataSource={data}
          rowSelection={{
            selectedRowKeys: currentPageSelectedKeys,
            onChange: handleSelectionChange,
          }}
         // scroll={getScrollConfig()}
          pagination={false}
          size={isTablet ? "small" : "middle"}
          rowKey={(record) => record.key}
          rowClassName={(record) => (!record.is_read ? "unread-row" : "")}
        />
      )}

      {/* ─── Bottom bar: selection indicator + pagination ─── */}
      <div className="bottom-bar-wrapper">
        {selectedCount > 0 && (
          <div className="selection-indicator">
            <span className="selection-indicator-text">
              Selected <strong>{selectedCount}</strong> total
              {currentPageSelectedCount > 0 && selectedCount > currentPageSelectedCount && (
                <> ({currentPageSelectedCount} on this page)</>
              )}
            </span>
            <Button
              type="link"
              size="small"
              onClick={clearAllSelections}
              className="selection-clear-btn"
            >
              Clear all selections
            </Button>
          </div>
        )}

        <div className="pagination-wrapper">
          <TablePagination
            page={page}
            onBack={onBack}
            onNext={onNext}
            resetCallback={resetCallback}
          />
        </div>
      </div>
    </div>
  );
}